import React from "react";
import { IHomeLeaguesFormData, ILeagueMgmtFieldData } from "../../types/forms";
import { SubmitHandler, useFieldArray, useForm } from "react-hook-form";
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import { displayFormAlert } from "../../lib/utils";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { store } from "../..";
import { tournamentManagementActions } from "../../features/tourneymgmt/tournamentManagementSlice";

export const loader = async () => {
    const homeLeagues = store.getState().tournamentmanagement.homeLeagues;

    const homeLeaguesPromise = homeLeagues.length
        ? Promise.resolve()
        : store.dispatch(tournamentManagementActions.getHomeLeagues());

    await Promise.all([homeLeaguesPromise]);

    return null;
};

const HomeLeagues = (): React.JSX.Element => {
    const dispatch = useAppDispatch();
    const homeLeagues = useAppSelector(
        state => state.tournamentmanagement.homeLeagues,
    );

    const homeLeagueFormSchema = yup.object().shape({
        leagues: yup
            .array(
                yup.object().shape({
                    id: yup.number().required(),
                    name: yup
                        .string()
                        .required(
                            "Please provide a league name or delete if not needed",
                        ),
                }),
            )
            .required(),
    });

    const {
        register,
        formState: { errors },
        handleSubmit,
        control,
    } = useForm<IHomeLeaguesFormData>({
        mode: "onChange",
        resolver: yupResolver(homeLeagueFormSchema),
        defaultValues: {
            leagues: homeLeagues.length
                ? homeLeagues.map(l => ({
                      id: l.id,
                      name: l.name,
                  }))
                : [],
        },
    });

    const {
        fields: leaguesList,
        append,
        remove,
    } = useFieldArray({
        control,
        name: "leagues",
    });

    const onSubmit: SubmitHandler<IHomeLeaguesFormData> = async data => {
        await dispatch(tournamentManagementActions.updateHomeLeagues(data));
    };

    return (
        <section
            className="homeLeaguesContent"
            data-testid="homeLeaguesContent"
        >
            <Form onSubmit={handleSubmit(onSubmit)}>
                <Row>
                    <Col>
                        <Table>
                            <tbody>
                                {leaguesList.map((league, index) => (
                                    <tr key={league.id}>
                                        <td width={"75%"}>
                                            <Form.Control
                                                type="text"
                                                {...register(
                                                    `leagues.${index}.name` as const,
                                                )}
                                                placeholder="League name"
                                                aria-invalid={
                                                    errors.leagues &&
                                                    errors.leagues[index]
                                                        ? "true"
                                                        : "false"
                                                }
                                            />
                                            {errors.leagues &&
                                                errors.leagues[index] &&
                                                displayFormAlert({
                                                    level: "danger",
                                                    text: errors.leagues[index]
                                                        ?.name
                                                        ?.message as string,
                                                })}
                                        </td>
                                        <td>
                                            <Button
                                                type="button"
                                                className="no-margin-btn"
                                                onClick={() => remove(index)}
                                            >
                                                Delete League
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                        <Button
                            type="button"
                            onClick={() =>
                                append({
                                    id: 0,
                                    name: "",
                                })
                            }
                        >
                            Add New League
                        </Button>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Button variant="primary" type="submit">
                            Update Leagues
                        </Button>
                    </Col>
                </Row>
            </Form>
        </section>
    );
};

export default HomeLeagues;
